import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle } from "../shared/styles";
import { TabBar } from "../shared/TabBar";
import { BackButton } from "../shared/BackButton";
import { LuPlus, LuX, LuTrash2, LuPackage, LuTriangleAlert } from "react-icons/lu";

// Parsed with an explicit midnight: a bare YYYY-MM-DD is read as UTC and would
// render as the previous day in Australian time.
const formatDate = (d) =>
  d
    ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// Work is sometimes logged retrospectively, so the picker reaches three years
// back. Forward is left open so a follow-up can be dated ahead.
const MIN_DATE = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 3);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
})();

const fmt2 = (n) =>
  Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Quantities and hours are decimals but usually whole — don't print "20.00".
const fmtNum = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : String(parseFloat(v.toFixed(2)));
};

// Editability shows on focus rather than with a permanent box. The transparent
// bottom border reserves that space so focusing a field never shifts the row.
const focusOn = (e) => {
  e.currentTarget.style.backgroundColor = "#f8fafc";
  e.currentTarget.style.borderBottomColor = colors.blue;
};
const focusOff = (e) => {
  e.currentTarget.style.backgroundColor = "transparent";
  e.currentTarget.style.borderBottomColor = "transparent";
};

const iconBtnStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  borderRadius: "4px",
  color: colors.slate,
  display: "flex",
  alignItems: "center",
};

// ── Materials rollup (all sites) ──────────────────────────────────────────────
//
// Groups line items so a bulk order can be placed straight off the list:
// "4.5Kg ABE Extinguisher x20 — $600". Rows with a material group on that FK so
// differently worded tasks using the same item still combine; rows without one
// fall back to their task text.
function rollupMaterials(followUps, { includeCompleted = false } = {}) {
  const map = {};
  followUps.forEach((fu) => {
    if (!includeCompleted && fu.status === "completed") return;
    (fu.items || []).forEach((it) => {
      if (!includeCompleted && it.completed) return;
      // Free-text tasks aren't purchasable — they're things to do, not things
      // to buy, and they already live on the Follow-Ups tab.
      if (!it.service_price_item) return;
      const label = it.service_price_item_name;
      if (!label) return;
      const key = `p:${it.service_price_item}`;
      if (!map[key]) {
        map[key] = {
          key,
          description: label,
          qty: 0,
          cost: 0,
          missingCost: false,
        };
      }
      const g = map[key];
      g.qty += parseFloat(it.quantity) || 0;
      g.cost += parseFloat(it.material_cost) || 0;
      if (!it.has_cost) g.missingCost = true;
    });
  });
  return Object.values(map)
    .map((g) => ({ ...g, unitCost: g.qty > 0 ? g.cost / g.qty : 0 }))
    .sort((a, b) => b.cost - a.cost || a.description.localeCompare(b.description));
}

function MaterialsSummary({ followUps, includeCompleted, onToggleCompleted }) {
  const rows = useMemo(
    () => rollupMaterials(followUps, { includeCompleted }),
    [followUps, includeCompleted]
  );
  const totalCost = rows.reduce((a, r) => a + r.cost, 0);
  const cols = "3fr 90px 110px 130px";

  return (
    <div style={styles.card}>
      <div style={styles.searchBar}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: colors.slate }}>
          <LuPackage size={16} />
          Materials still required across all sites
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: colors.slate, cursor: "pointer" }}>
          <input type="checkbox" checked={includeCompleted} onChange={(e) => onToggleCompleted(e.target.checked)} />
          Include completed
        </label>
      </div>
      {rows.length === 0 ? (
        <div style={styles.loading}>Nothing outstanding.</div>
      ) : (
        <>
          <div style={{ ...styles.tableHeader, gridTemplateColumns: cols, gap: "12px" }}>
            <div>Item</div>
            <div style={{ textAlign: "right" }}>Qty</div>
            <div style={{ textAlign: "right" }}>Unit Cost</div>
            <div style={{ textAlign: "right" }}>Subtotal</div>
          </div>
          {rows.map((r) => (
            <div
              key={r.key}
              style={{
                display: "grid",
                gridTemplateColumns: cols,
                gap: "12px",
                padding: "12px 20px",
                borderBottom: `1px solid ${colors.border}`,
                alignItems: "center",
                fontSize: "14px",
              }}
            >
              <div style={{ fontWeight: "600", color: colors.text }}>{r.description}</div>
              <div style={{ textAlign: "right", fontWeight: "700" }}>{fmtNum(r.qty)}</div>
              <div style={{ textAlign: "right", color: colors.slate }}>${fmt2(r.unitCost)}</div>
              <div style={{ textAlign: "right", fontWeight: "600", color: r.missingCost ? colors.amber : colors.text }}>
                ${fmt2(r.cost)}
                {r.missingCost && <span title="Cost not set in Service Prices"> *</span>}
              </div>
            </div>
          ))}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: cols,
              gap: "12px",
              padding: "14px 20px",
              backgroundColor: colors.darkGrey,
              color: colors.white,
              fontWeight: "700",
              fontSize: "14px",
              borderRadius: "0 0 12px 12px",
            }}
          >
            <div>TOTAL</div>
            <div />
            <div />
            <div style={{ textAlign: "right" }}>${fmt2(totalCost)}</div>
          </div>
          {rows.some((r) => r.missingCost) && (
            <div style={{ padding: "10px 20px", fontSize: "12px", color: colors.amber }}>
              * Cost (ex. GST) not yet set on the Service Prices page — these lines count as
              $0.00 until it is.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Work item row ─────────────────────────────────────────────────────────────
//
// Edited in place: the task is a large free-text box, with the material,
// quantity, cost and hours beside it as columns. Text and number fields save on
// blur; the material dropdown and the done tick save immediately.
function ItemRow({ item, index, prices, cols, onChanged }) {
  const [description, setDescription] = useState(item.description || "");
  const [quantity, setQuantity] = useState(fmtNum(item.quantity));
  const [hours, setHours] = useState(fmtNum(item.labour_hours));
  const [cost, setCost] = useState(item.has_cost ? fmt2(item.material_cost) : "");
  const [busy, setBusy] = useState(false);

  // Re-sync when the list refetches — e.g. after a cost is entered in Service
  // Prices, which changes this row's figure without anyone editing it.
  useEffect(() => {
    setDescription(item.description || "");
    setQuantity(fmtNum(item.quantity));
    setHours(fmtNum(item.labour_hours));
    setCost(item.has_cost ? fmt2(item.material_cost) : "");
  }, [item.id, item.description, item.quantity, item.labour_hours, item.material_cost, item.has_cost]);

  const patch = async (payload) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/follow-up-items/${item.id}/`, payload);
      await onChanged();
    } catch {
      /* leave the field as typed; the next save retries */
    }
    setBusy(false);
  };

  const saveIfChanged = (field, value, current) => {
    if (String(value ?? "") === String(current ?? "")) return;
    patch({ [field]: value });
  };

  // The Materials column edits the line total, but the override is stored per
  // unit so that changing the quantity rescales it. Clearing the box hands the
  // row back to the price book.
  const saveCost = () => {
    const typed = cost.trim().replace(/[$,]/g, "");
    if (typed === "") {
      if (item.unit_material_cost !== null && item.unit_material_cost !== undefined) {
        patch({ unit_material_cost: null });
      }
      return;
    }
    const lineTotal = parseFloat(typed);
    if (isNaN(lineTotal)) return;
    if (Math.abs(lineTotal - Number(item.material_cost)) < 0.005) return;
    const qty = parseFloat(quantity) || 1;
    patch({ unit_material_cost: (lineTotal / qty).toFixed(2) });
  };

  // Every cell shares one style at one height, so the row reads as a list line
  // rather than a form. Editability shows on focus instead of with a permanent
  // box; the transparent bottom border reserves that space so focusing a field
  // never shifts the row.
  const cellBase = {
    width: "100%",
    height: "30px",
    boxSizing: "border-box",
    border: "none",
    borderBottom: "1px solid transparent",
    borderRadius: 0,
    background: "transparent",
    padding: "0 4px",
    margin: 0,
    fontSize: "14px",
    fontFamily: "inherit",
    color: colors.text,
    outline: "none",
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: "10px",
        padding: "6px 20px",
        borderBottom: `1px solid ${colors.border}`,
        alignItems: "center",
        opacity: item.completed ? 0.55 : busy ? 0.7 : 1,
        transition: "opacity 0.15s",
      }}
    >
      <div style={{ fontSize: "13px", fontWeight: "600", color: colors.slate }}>
        {index + 1}.
      </div>

      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onFocus={focusOn}
        onBlur={(e) => {
          focusOff(e);
          saveIfChanged("description", description, item.description);
        }}
        placeholder="Describe the work required..."
        style={{
          ...cellBase,
          textOverflow: "ellipsis",
          textDecoration: item.completed ? "line-through" : "none",
        }}
      />

      <select
        value={item.service_price_item || ""}
        onChange={(e) => patch({ service_price_item: e.target.value ? Number(e.target.value) : null })}
        onFocus={focusOn}
        onBlur={focusOff}
        style={cellBase}
      >
        <option value="">— none —</option>
        {prices.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.cost_ex_gst != null ? ` — $${fmt2(p.cost_ex_gst)}` : " — no cost set"}
          </option>
        ))}
      </select>

      <input
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        onFocus={focusOn}
        onBlur={(e) => {
          focusOff(e);
          saveIfChanged("quantity", parseFloat(quantity) || 0, item.quantity);
        }}
        type="number"
        step="0.01"
        min="0"
        style={{ ...cellBase, textAlign: "right" }}
      />

      <input
        value={cost}
        onChange={(e) => setCost(e.target.value)}
        onFocus={focusOn}
        onBlur={(e) => {
          focusOff(e);
          saveCost();
        }}
        placeholder="—"
        title={
          item.has_cost
            ? "Comes from the material's Cost in Service Prices. Type here to override this line; clear it to go back."
            : "No Cost (ex. GST) set for this material in Service Prices, so it counts as $0.00. Type an amount here to override just this line."
        }
        style={{
          ...cellBase,
          textAlign: "right",
          color: item.has_cost ? colors.text : colors.amber,
          fontWeight: "600",
        }}
      />

      <input
        value={hours}
        onChange={(e) => setHours(e.target.value)}
        onFocus={focusOn}
        onBlur={(e) => {
          focusOff(e);
          saveIfChanged("labour_hours", parseFloat(hours) || 0, item.labour_hours);
        }}
        type="number"
        step="0.25"
        min="0"
        style={{ ...cellBase, textAlign: "right" }}
      />

      <div style={{ textAlign: "center" }}>
        <input
          type="checkbox"
          checked={item.completed}
          onChange={() => patch({ completed: !item.completed })}
          style={{ cursor: "pointer", width: "16px", height: "16px" }}
          title="Tick when this item is done"
        />
      </div>

      <div style={{ display: "flex" }}>
        <button
          onClick={async () => {
            if (!window.confirm("Delete this line?")) return;
            await axios.delete(`${API}/follow-up-items/${item.id}/`);
            onChanged();
          }}
          title="Delete line"
          style={iconBtnStyle}
        >
          <LuTrash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Follow-up detail ──────────────────────────────────────────────────────────

function FollowUpDetail({ followUp, prices, onBack, onChanged }) {
  const [title, setTitle] = useState(followUp.title || "");
  const [date, setDate] = useState(followUp.date || "");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [adding, setAdding] = useState(false);

  const items = followUp.items || [];
  const missing = followUp.items_missing_cost || 0;

  const saveDetails = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      await axios.patch(`${API}/follow-ups/${followUp.id}/`, { title, date });
      await onChanged();
      setSavedMsg("Saved");
      setTimeout(() => setSavedMsg(""), 2000);
    } catch {
      setSavedMsg("Save failed");
    }
    setSaving(false);
  };

  const addItem = async () => {
    setAdding(true);
    try {
      await axios.post(`${API}/follow-up-items/`, {
        follow_up: followUp.id,
        description: "",
        quantity: 1,
        labour_hours: 0,
      });
      await onChanged();
    } catch {
      /* the button can simply be pressed again */
    }
    setAdding(false);
  };

  // # | Task | Material | Qty | Materials | Hours | tick | delete
  const cols = "28px minmax(220px, 3fr) minmax(190px, 2fr) 70px 100px 70px 44px 40px";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
          <BackButton onClick={onBack} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Follow-Up name"
                onFocus={focusOn}
                onBlur={focusOff}
                style={{
                  width: "auto",
                  minWidth: "280px",
                  border: "none",
                  borderBottom: "1px solid transparent",
                  borderRadius: 0,
                  background: "transparent",
                  padding: "2px 4px",
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "700",
                  fontFamily: "inherit",
                  color: colors.text,
                  outline: "none",
                }}
              />
              <input
                type="date"
                value={date}
                min={MIN_DATE}
                onChange={(e) => setDate(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                title="The day this work was raised"
                style={{
                  border: "none",
                  borderBottom: "1px solid transparent",
                  borderRadius: 0,
                  background: "transparent",
                  padding: "2px 4px",
                  margin: 0,
                  fontSize: "14px",
                  fontFamily: "inherit",
                  color: colors.slate,
                  outline: "none",
                }}
              />
            </div>
            <div style={{ fontSize: "13px", color: colors.slate, marginTop: "4px" }}>
              {followUp.site_name} · {followUp.site_address} · {followUp.account_name}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {savedMsg && (
            <span style={{ fontSize: "13px", color: savedMsg === "Saved" ? colors.green : colors.red }}>
              {savedMsg}
            </span>
          )}
          <button onClick={saveDetails} disabled={saving} style={styles.addButton}>
            {saving ? "Saving..." : "Save details"}
          </button>
        </div>
      </div>

      <div style={styles.content}>
        <div style={{ ...styles.card, marginBottom: "16px" }}>
          {missing > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                backgroundColor: "#fffbeb",
                borderBottom: `1px solid ${colors.border}`,
                fontSize: "13px",
                color: "#92400e",
              }}
            >
              <LuTriangleAlert size={15} />
              {missing} line{missing !== 1 ? "s have" : " has"} no material cost — set Cost
              (ex. GST) on the Service Prices page, or type an amount straight into the
              Materials column.
            </div>
          )}

          <div style={{ ...styles.tableHeader, gridTemplateColumns: cols, gap: "10px" }}>
            <div>#</div>
            <div>Requirements / Tasks</div>
            <div>Material</div>
            <div style={{ textAlign: "right" }}>Qty</div>
            <div style={{ textAlign: "right" }}>Materials</div>
            <div style={{ textAlign: "right" }}>Hours</div>
            <div style={{ textAlign: "center" }}>✓</div>
            <div />
          </div>

          {items.length === 0 ? (
            <div style={styles.loading}>No works listed yet. Add the first item.</div>
          ) : (
            items.map((it, i) => (
              <ItemRow
                key={it.id}
                item={it}
                index={i}
                prices={prices}
                cols={cols}
                onChanged={onChanged}
              />
            ))
          )}

          <div style={{ padding: "12px 20px", borderBottom: `1px solid ${colors.border}` }}>
            <button onClick={addItem} disabled={adding} style={styles.addButton}>
              <LuPlus size={15} /> {adding ? "Adding..." : "Add Item"}
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "28px",
              padding: "16px 20px",
              fontSize: "15px",
              fontWeight: "700",
              color: colors.text,
            }}
          >
            <span style={{ letterSpacing: "0.5px" }}>SITE TOTALS</span>
            <span>${fmt2(followUp.total_material_cost)}</span>
            <span>{fmtNum(followUp.total_labour_hours)} hrs</span>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function FollowUpsPage() {
  const [followUps, setFollowUps] = useState([]);
  const [sites, setSites] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState("list");
  const [search, setSearch] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [includeCompleted, setIncludeCompleted] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");
  const [siteResults, setSiteResults] = useState([]);
  const [createSite, setCreateSite] = useState(null);
  const [createTitle, setCreateTitle] = useState("");
  const [createDate, setCreateDate] = useState(todayISO());
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  useEffect(() => {
    loadAll();
    axios.get(`${API}/sites/`).then((r) => setSites(r.data || [])).catch(() => {});
    axios
      .get(`${API}/service-prices/?active=true`)
      .then((r) => setPrices(r.data || []))
      .catch(() => {});
  }, []);

  const loadAll = () => {
    setLoading(true);
    return axios
      .get(`${API}/follow-ups/`)
      .then((r) => {
        setFollowUps(r.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (siteSearch.length < 2) {
      setSiteResults([]);
      return;
    }
    const q = siteSearch.toLowerCase();
    setSiteResults(
      sites
        .filter(
          (s) => s.site_name.toLowerCase().includes(q) || s.site_address.toLowerCase().includes(q)
        )
        .slice(0, 8)
    );
  }, [siteSearch, sites]);

  const resetCreate = () => {
    setCreateSite(null);
    setSiteSearch("");
    setSiteResults([]);
    setCreateTitle("");
    setCreateDate(todayISO());
    setCreateError("");
  };

  const handleCreate = async () => {
    if (!createSite) {
      setCreateError("Please select a site.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const res = await axios.post(`${API}/follow-ups/`, {
        site: createSite.id,
        title: createTitle.trim(),
        date: createDate,
      });
      setShowCreate(false);
      resetCreate();
      await loadAll();
      setSelectedId(res.data.id);
    } catch {
      setCreateError("Could not create the follow-up. Please try again.");
    }
    setCreating(false);
  };

  const handleDelete = async (fu, e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Delete this follow-up for ${fu.site_name}? Its ${fu.item_count} item(s) will go too.`
      )
    )
      return;
    await axios.delete(`${API}/follow-ups/${fu.id}/`);
    loadAll();
  };

  const byDate = (a, b) => (a.date || "").localeCompare(b.date || "") || a.id - b.id;
  const filtered = followUps.filter((f) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!`${f.site_name} ${f.account_name} ${f.title}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }).sort(byDate);

  const selected = followUps.find((f) => f.id === selectedId);
  if (selected) {
    return (
      <FollowUpDetail
        followUp={selected}
        prices={prices}
        onBack={() => setSelectedId(null)}
        onChanged={loadAll}
      />
    );
  }

  const totalMaterials = filtered.reduce((a, f) => a + (Number(f.total_material_cost) || 0), 0);
  const totalHours = filtered.reduce((a, f) => a + (Number(f.total_labour_hours) || 0), 0);
  const cols = "110px 2fr 2fr 120px 90px 44px";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Follow-Ups</div>
        <button
          onClick={() => {
            resetCreate();
            setShowCreate(true);
          }}
          style={styles.addButton}
        >
          <LuPlus size={15} /> New Follow-Up
        </button>
      </div>

      <TabBar
        tabs={["list", "materials"]}
        tabLabels={{ list: "Follow-Ups", materials: "Materials" }}
        activeTab={tab}
        onSelect={setTab}
      />

      {tab === "materials" ? (
        <div style={styles.content}>
          <MaterialsSummary
            followUps={followUps}
            includeCompleted={includeCompleted}
            onToggleCompleted={setIncludeCompleted}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: colors.white,
              borderBottom: `1px solid ${colors.border}`,
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              style={{ ...inputStyle, flex: 1, minWidth: "200px", width: "auto" }}
              placeholder="Search site, customer or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={styles.content}>
            <div style={styles.card}>
              {loading ? (
                <div style={styles.loading}>Loading...</div>
              ) : filtered.length === 0 ? (
                <div style={styles.loading}>No follow-ups found.</div>
              ) : (
                <>
                  <div style={{ ...styles.tableHeader, gridTemplateColumns: cols, gap: "12px" }}>
                    <div>Date</div>
                    <div>Site</div>
                    <div>Tasks</div>
                    <div style={{ textAlign: "right" }}>Materials</div>
                    <div style={{ textAlign: "right" }}>Time</div>
                    <div />
                  </div>
                  {filtered.map((f) => (
                    <div
                      key={f.id}
                      onClick={() => setSelectedId(f.id)}
                      onMouseEnter={() => setHoveredRow(f.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: cols,
                        gap: "12px",
                        padding: "12px 20px",
                        borderBottom: `1px solid ${colors.border}`,
                        alignItems: "center",
                        cursor: "pointer",
                        fontSize: "14px",
                        backgroundColor: hoveredRow === f.id ? "#f8fafc" : colors.white,
                      }}
                    >
                      <div style={{ fontSize: "13px", color: colors.slate, whiteSpace: "nowrap" }}>
                        {formatDate(f.date)}
                      </div>
                      <div style={{ overflow: "hidden" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            color: colors.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.site_name}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: colors.slate,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.account_name}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: f.title ? colors.text : colors.slate,
                          fontStyle: f.title ? "normal" : "italic",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {f.title || "Untitled"}
                      </div>
                      <div
                        style={{
                          textAlign: "right",
                          fontWeight: "600",
                          color: f.items_missing_cost ? colors.amber : colors.text,
                        }}
                      >
                        ${fmt2(f.total_material_cost)}
                      </div>
                      <div style={{ textAlign: "right" }}>{fmtNum(f.total_labour_hours)}</div>
                      <div
                        style={{
                          display: "flex",
                          gap: "2px",
                          opacity: hoveredRow === f.id ? 1 : 0,
                          transition: "opacity 0.15s",
                        }}
                      >
                        <button
                          onClick={(e) => handleDelete(f, e)}
                          title="Delete"
                          style={iconBtnStyle}
                        >
                          <LuTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: cols,
                      gap: "12px",
                      padding: "14px 20px",
                      backgroundColor: colors.darkGrey,
                      color: colors.white,
                      fontWeight: "700",
                      fontSize: "14px",
                      borderRadius: "0 0 12px 12px",
                    }}
                  >
                    <div />
                    <div />
                    <div style={{ textAlign: "right", letterSpacing: "0.5px" }}>TOTALS</div>
                    <div style={{ textAlign: "right" }}>${fmt2(totalMaterials)}</div>
                    <div style={{ textAlign: "right" }}>{fmtNum(totalHours)}</div>
                    <div />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {showCreate && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: colors.white,
              borderRadius: "12px",
              width: "500px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: `1px solid ${colors.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: "700", fontSize: "16px", color: colors.text }}>
                New Follow-Up
              </div>
              <button
                onClick={() => {
                  setShowCreate(false);
                  resetCreate();
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: colors.slate,
                  padding: "4px",
                  lineHeight: 1,
                }}
              >
                <LuX size={18} />
              </button>
            </div>
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div>
                <label style={labelStyle}>Site *</label>
                {createSite ? (
                  <div
                    style={{
                      padding: "10px 14px",
                      border: `2px solid ${colors.teal}`,
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "#f0fafa",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "14px" }}>{createSite.site_name}</div>
                      <div style={{ fontSize: "12px", color: colors.slate }}>
                        {createSite.site_address}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setCreateSite(null);
                        setSiteSearch("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: colors.slate,
                        fontSize: "16px",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    <input
                      style={inputStyle}
                      placeholder="Search by site name or address..."
                      value={siteSearch}
                      onChange={(e) => setSiteSearch(e.target.value)}
                    />
                    {siteResults.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "6px",
                          backgroundColor: colors.white,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          zIndex: 10,
                        }}
                      >
                        {siteResults.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => {
                              setCreateSite(s);
                              setSiteSearch("");
                              setSiteResults([]);
                            }}
                            style={{
                              padding: "10px 14px",
                              cursor: "pointer",
                              borderBottom: `1px solid ${colors.border}`,
                              fontSize: "14px",
                            }}
                          >
                            <div style={{ fontWeight: "600" }}>{s.site_name}</div>
                            <div style={{ fontSize: "12px", color: colors.slate }}>
                              {s.site_address}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Follow-Up Name</label>
                <input
                  style={inputStyle}
                  placeholder="e.g. Level 2 upgrade"
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Date</label>
                <input
                  type="date"
                  style={inputStyle}
                  value={createDate}
                  min={MIN_DATE}
                  onChange={(e) => setCreateDate(e.target.value)}
                />
              </div>
              {createError && <div style={{ color: colors.red, fontSize: "13px" }}>{createError}</div>}
            </div>
            <div
              style={{
                padding: "16px 24px",
                borderTop: `1px solid ${colors.border}`,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <button
                onClick={() => {
                  setShowCreate(false);
                  resetCreate();
                }}
                style={{
                  background: "none",
                  border: `1px solid ${colors.border}`,
                  borderRadius: "6px",
                  padding: "8px 20px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button onClick={handleCreate} disabled={creating} style={styles.addButton}>
                {creating ? "Creating..." : "Create Follow-Up"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
